import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/zera/Navbar';
import PageHero from '@/components/zera/PageHero';
import Footer from '@/components/zera/Footer';
import { Bed, Bath, Users, MapPin, ArrowRight, X, Wifi, Car, Waves, UtensilsCrossed } from 'lucide-react';

const villas = [
  {
    name: 'Villa Juna Beach',
    location: 'Saranda, Vlorë County',
    description: 'An exclusive beachfront villa on the Saranda coast offering direct access to the Ionian Sea, panoramic terraces, and sweeping views toward Corfu. Highly rated for its pristine private beach, modern design, and attentive hospitality just minutes from Saranda town.',
    image: 'https://images.unsplash.com/photo-1615571022219-eb45cf7faa9d?auto=format&fit=crop&w=2400&q=90',
    bedrooms: 5,
    bathrooms: 4,
    guests: 10,
    features: ['Private Beach', 'Sea View', 'Infinity Pool', 'Beach Access'],
  },
  {
    name: 'ARAMERAS Resort',
    location: 'Borsh, Albanian Riviera',
    description: 'A luxurious Riviera resort nestled between olive groves and the turquoise Ionian coast. Renowned for its elegant suites, serene beachfront gardens, wellness spa, and award-winning Mediterranean cuisine overlooking Borsh Beach.',
    image: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?auto=format&fit=crop&w=2400&q=90',
    bedrooms: 6,
    bathrooms: 6,
    guests: 12,
    features: ['Beach Access', 'Spa & Wellness', 'Sea View', 'Private Chef'],
  },
  {
    name: 'Hotel Villa Margarit',
    location: 'Saranda, Vlorë County',
    description: 'A boutique luxury villa perched above Saranda Bay, celebrated by TripAdvisor travelers for its breathtaking sea views, rooftop pool, and refined suites blending contemporary elegance with warm Albanian hospitality.',
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=2400&q=90',
    bedrooms: 5,
    bathrooms: 5,
    guests: 10,
    features: ['Rooftop Pool', 'Sea View', 'Heritage Building', 'Concierge'],
  },
  {
    name: 'Vila Aaron',
    location: 'Ksamil, Vlorë County',
    description: 'A highly rated family-run villa in the heart of Ksamil, steps from the turquoise coves and Four Islands. Guests praise its immaculate rooms, sun-drenched terrace, and warm, personalized service just moments from the beach.',
    image: 'https://images.unsplash.com/photo-1602343168117-bb8ffe3e2e9f?auto=format&fit=crop&w=2400&q=90',
    bedrooms: 4,
    bathrooms: 4,
    guests: 8,
    features: ['Beach Access', 'Sun Terrace', 'Sea View', 'Garden Terrace'],
  },
];




const amenityIcons: Record<string, React.FC<{ className?: string }>> = {
  'Infinity Pool': Waves,
  'Beach Access': Waves,
  'Chef Service': UtensilsCrossed,
  'Sea View': MapPin,
  'Heritage Building': MapPin,
  'City Views': MapPin,
  'Garden Terrace': MapPin,
  'Wine Cellar': UtensilsCrossed,
  'Mountain Views': MapPin,
  'Sauna': Waves,
  'Fireplace': MapPin,
  'Hiking Access': MapPin,
  'Heated Pool': Waves,
  'Cliff Views': MapPin,
  'Private Chef': UtensilsCrossed,
  'Boat Dock': Waves,
};

const VillaRentals: React.FC = () => {
  const navigate = useNavigate();
  const [selectedVilla, setSelectedVilla] = useState<typeof villas[0] | null>(null);

  return (
    <div className="min-h-screen bg-[#FAF8F3]">
      <Navbar />

      <PageHero
        image="https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1775237744873_389b5d0f.png"
        title={"Private Villa\nCollection"}
        subtitle="Handpicked luxury villas across Albania — from beachfront estates to mountain retreats, each with dedicated staff and bespoke services."
        badge="Villa Rentals"
        height="h-screen"
      />

      {/* Villa Listings */}
      <section className="py-20 lg:py-32">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12">
            <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">Our Collection</span>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1A3B5C] font-light mt-2">Exclusive Properties</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {villas.map((villa) => (

              <div key={villa.name} className="group bg-white border border-[#E8DFD0] hover:border-[#C9A961]/30 transition-all duration-500 overflow-hidden">
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={villa.image} alt={villa.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <button
                    onClick={() => navigate('/booking')}
                    className="absolute top-4 left-4 font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-2 bg-[#1A3B5C]/10 backdrop-blur-sm text-[#1A3B5C] hover:bg-[#1A3B5C]/20 transition-colors"
                  >
                    Book Now
                  </button>
                </div>

                <div className="p-8">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-3 h-3 text-[#C9A961]" />
                    <span className="font-sans text-[10px] tracking-wider uppercase text-[#999]">{villa.location}</span>
                  </div>
                  <h3 className="font-serif text-xl text-[#1A3B5C] font-light mb-3">{villa.name}</h3>
                  <p className="font-sans text-sm text-[#666] leading-relaxed mb-4">{villa.description}</p>

                  <div className="flex items-center gap-6 mb-4">
                    <div className="flex items-center gap-1.5">
                      <Bed className="w-3.5 h-3.5 text-[#C9A961]" />
                      <span className="font-sans text-xs text-[#666]">{villa.bedrooms} Beds</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Bath className="w-3.5 h-3.5 text-[#C9A961]" />
                      <span className="font-sans text-xs text-[#666]">{villa.bathrooms} Baths</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5 text-[#C9A961]" />
                      <span className="font-sans text-xs text-[#666]">{villa.guests} Guests</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {villa.features.map((f) => (
                      <span key={f} className="font-sans text-[9px] tracking-wider uppercase px-3 py-1 bg-[#F5F1E8] text-[#999]">{f}</span>
                    ))}
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setSelectedVilla(villa)}
                      className="flex-1 font-sans text-[10px] tracking-[0.2em] uppercase py-3 border border-[#1A3B5C] text-[#1A3B5C] hover:bg-[#1A3B5C] hover:text-white transition-all text-center"
                    >
                      View Details
                    </button>
                    <button
                      onClick={() => navigate('/booking')}
                      className="flex-1 font-sans text-[10px] tracking-[0.2em] uppercase py-3 bg-[#C9A961] text-white hover:bg-[#b8944d] transition-all text-center"
                    >
                      Reserve
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* Villa Detail Modal */}
      {selectedVilla && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setSelectedVilla(null)}>
          <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="relative aspect-video">
              <img src={selectedVilla.image} alt={selectedVilla.name} className="w-full h-full object-cover" />
              <button onClick={() => setSelectedVilla(null)} className="absolute top-4 right-4 w-10 h-10 bg-white/90 flex items-center justify-center hover:bg-white transition-colors">
                <X className="w-5 h-5 text-[#1A3B5C]" />
              </button>
            </div>
            <div className="p-10">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-3 h-3 text-[#C9A961]" />
                <span className="font-sans text-[10px] tracking-wider uppercase text-[#999]">{selectedVilla.location}</span>
              </div>
              <div className="flex items-start justify-between mb-6">
                <h2 className="font-serif text-3xl text-[#1A3B5C] font-light">{selectedVilla.name}</h2>
                <button
                  onClick={() => { setSelectedVilla(null); navigate('/booking'); }}
                  className="font-sans text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 bg-[#1A3B5C]/10 text-[#1A3B5C] hover:bg-[#1A3B5C]/20 transition-colors"
                >
                  Book Now
                </button>
              </div>

              <p className="font-sans text-sm text-[#666] leading-relaxed mb-8">{selectedVilla.description}</p>
              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="text-center p-4 bg-[#F5F1E8]">
                  <span className="font-serif text-2xl text-[#1A3B5C]">{selectedVilla.bedrooms}</span>
                  <p className="font-sans text-[9px] tracking-wider uppercase text-[#999] mt-1">Bedrooms</p>
                </div>
                <div className="text-center p-4 bg-[#F5F1E8]">
                  <span className="font-serif text-2xl text-[#1A3B5C]">{selectedVilla.bathrooms}</span>
                  <p className="font-sans text-[9px] tracking-wider uppercase text-[#999] mt-1">Bathrooms</p>
                </div>
                <div className="text-center p-4 bg-[#F5F1E8]">
                  <span className="font-serif text-2xl text-[#1A3B5C]">{selectedVilla.guests}</span>
                  <p className="font-sans text-[9px] tracking-wider uppercase text-[#999] mt-1">Max Guests</p>
                </div>
              </div>
              <button onClick={() => { setSelectedVilla(null); navigate('/booking'); }} className="w-full font-sans text-[11px] tracking-[0.25em] uppercase py-4 bg-[#C9A961] text-white hover:bg-[#b8944d] transition-all">
                Reserve This Villa
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default VillaRentals;
